import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import type { CitationSource } from '@ctw/shared/content/fact-extraction/use-citation-sources';

export interface ExportGroupSummary {
  label: string;
  // Fields in this group that are documented AND not excluded (i.e. going in the export)
  documentedIncludedCount: number;
}

export interface ExportSummary {
  includedFieldsCount: number;
  excludedFieldsCount: number;
  // Included fields that were actually extracted (not "not documented")
  totalDocumentedIncludedCount: number;
  groupSummaries: Array<ExportGroupSummary>;
  // Deduped source documents cited by included fields
  includedSourcesCount: number;
  // Deduped source documents cited by any field in the report
  totalSourcesCount: number;
}

/**
 * Summarizes what an Atlas export would contain given the current exclusions.
 * Only citations that resolve to an actual source document (kind 'document')
 * count as sources; drawer-only and unresolved citations are ignored, matching
 * the table's greyed-out citation behavior. Sources are deduped by binaryId.
 * Exclusion only applies to documented fields — undocumented fields can't be
 * excluded in the UI, so a persisted exclusion of one is ignored.
 */
export function computeExportSummary({
  groups,
  excludedIds,
  sourceByCitationId,
}: {
  groups: Array<MappedFactGroup>;
  excludedIds: Array<string>;
  sourceByCitationId: ReadonlyMap<number, CitationSource>;
}): ExportSummary {
  const excluded = new Set(excludedIds);
  const allBinaryIds = new Set<string>();
  const includedBinaryIds = new Set<string>();
  let includedFieldsCount = 0;
  let excludedFieldsCount = 0;
  let totalDocumentedIncludedCount = 0;

  const groupSummaries = groups.map((group) => {
    let documentedIncludedCount = 0;
    for (const fact of group.facts) {
      const isExcluded = fact.isDocumented && excluded.has(fact.id);
      if (isExcluded) {
        excludedFieldsCount += 1;
      } else {
        includedFieldsCount += 1;
        if (fact.isDocumented) {
          documentedIncludedCount += 1;
          totalDocumentedIncludedCount += 1;
        }
      }
      for (const citationId of fact.citationIds) {
        const source = sourceByCitationId.get(citationId);
        if (source?.kind === 'document') {
          allBinaryIds.add(source.binaryId);
          if (!isExcluded) {
            includedBinaryIds.add(source.binaryId);
          }
        }
      }
    }
    return { label: group.label, documentedIncludedCount };
  });

  return {
    includedFieldsCount,
    excludedFieldsCount,
    totalDocumentedIncludedCount,
    groupSummaries,
    includedSourcesCount: includedBinaryIds.size,
    totalSourcesCount: allBinaryIds.size,
  };
}
